import { SystemWorkOrderStatus } from '../job/system.workorder.status';
import { WorkOrder } from '../job/workorder';
import { PendingCode } from '../shared/code';
import { isSorCode } from '../shared/code.type';
import { isItemRemoved } from '../../utilities/item.status.utility';
import { Task } from './task';
import { TaskLinkType, TaskTarget } from './task.enum';
import { readTaskGate, TaskGate } from './task.evidence';
import { summariseGateItems } from './gate.item.summary';
import { isPendingTask, isStampedTaskResolved } from './task.status.policy';

/** One task holding a stage open; the status name is the tenant's own, never fixed wording. */
export interface StageCompletionBlocker {
    taskId: string;
    name: string;
    statusName: string;
    /** Set when one refusal covers several stages, e.g. a whole job being completed. */
    stageNumber?: string;
}

export const STAGE_COMPLETION_BLOCKED_CODE = 'StageCompletionBlocked';

/**
 * The stage completion gate: status only, never evidence (evidence was checked when each task was completed).
 * Callers pass only the tasks the completing party can see; the Web App and Public API hide none.
 */
export function findStageCompletionBlockers(tasks: readonly Task[] | null | undefined): StageCompletionBlocker[] {

    return findUnresolvedGatedTasks(tasks, [TaskGate.StageCompletion]);
}

/**
 * The pending SOR line a Pending task stands for, when that line records work already done; otherwise undefined.
 * The stage gate service lets such a task through, once its evidence is checked, while the tenant allows it (WBS-13379).
 * A line approved, declined or withdrawn has left the pending set, so its task answers to the gate as usual.
 */
export function findWorkAlreadyCompletedLine(task: Task, stage: Pick<WorkOrder, 'claimCodes'>): PendingCode | undefined {

    if (!isPendingTask(task) || task.type !== TaskLinkType.Item || !task.data?._id) return undefined;

    return (stage.claimCodes ?? [])
        .filter(line => line.active !== false && !isItemRemoved(line, true))
        .filter(line => isSorCode(line) && line.workAlreadyCompleted === true)
        .find(line => line._id === task.data._id);
}

/**
 * The open, switched-on tasks whose "Must resolve before" is one of `gates`; shared by the stage and claim gates so "resolved" means the same to both.
 * A daily template is only a stamp for the real day tasks and is always open, so it never holds anything; its day instances do.
 */
export function findUnresolvedGatedTasks(tasks: readonly Task[] | null | undefined, gates: readonly TaskGate[]): StageCompletionBlocker[] {

    return (tasks ?? [])
        .filter(holdsStage)
        .filter(task => task.active !== false)
        .filter(task => !task.daily || task.instance === true)
        .filter(task => gates.includes(readTaskGate(task)))
        .filter(task => !isStampedTaskResolved(task))
        .map(task => ({ taskId: task._id, name: task.name ?? task.description ?? '', statusName: task.taskStatus?.name ?? task.status ?? '' }));
}

/**
 * Only the stage's own Field tasks hold it or its claim: a sub-task answers to its parent task, and a pending line hid its item sub-tasks while they still held the stage (WBS-12388);
 * an Office/Supervisor task is the office's own check, which the contractor can neither see nor resolve (WBS-12380).
 */
function holdsStage(task: Task): boolean {

    return task.parent?.type === TaskLinkType.WorkOrder && task.target === TaskTarget.Field;
}

/** "Install meter (Open), Safety check (Pending)": the refusal names each task with the tenant's status, up to the first five. */
export function describeStageCompletionBlockers(blockers: readonly StageCompletionBlocker[]): string {

    const tasks = blockers.map(blocker => `${blocker.stageNumber ? `${blocker.stageNumber}: ` : ''}${blocker.name} (${blocker.statusName})`);

    return `Stage cannot be completed while these tasks are unresolved: ${summariseGateItems(tasks, ['task', 'tasks'])}`;
}

const STAGE_STATUSES_A_JOB_COMPLETION_LEAVES: readonly string[] = [SystemWorkOrderStatus.Completed, SystemWorkOrderStatus.Finalised, SystemWorkOrderStatus.Closed, SystemWorkOrderStatus.Canceled];

/** The stages completing a whole job would move to Completed; the rest are already done or out of play, so they never hold the job. */
export function stagesMovedByJobCompletion(stages: readonly WorkOrder[] | null | undefined): WorkOrder[] {

    return (stages ?? []).filter(stage => !STAGE_STATUSES_A_JOB_COMPLETION_LEAVES.includes(stage.status?.systemStatus));
}
